import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, CheckCircle2, ChevronRight, Clock, Search } from 'lucide-react';
import { env } from '@config/env';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { Banner } from '@shared/ui/feedback/Banner';
import {
  FormSection,
  TextInputField,
  SelectField,
  CheckboxOption,
  ActionButton,
} from '@shared/ui/forms/FormFields';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useAuthStore } from '@shared/stores/authStore';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { setJoinTenantContext } from '@shared/api/joinTenantContext';
import { toApiError } from '@shared/api/errors';
import { fetchMemberTypes, type MemberType } from '@features/auth/api';
import {
  searchTenantsByRoot,
  joinTenant,
  fetchJoinStatus,
  fetchJoinOnboardingGate,
  onboardingRouteForGate,
  type TenantSearchResult,
  type JoinRequestMemberData,
} from './api';
import { joinConfig } from './config';

const SEARCH_MIN = 3;

type Step = 'search' | 'details' | 'pending' | 'done';

/**
 * In-app "Join another community" — the React equivalent of the Angular
 * member-portal "Join a tenant" flow. Search a sibling community within the
 * SSO root scope, submit a join request against that tenant (addressed via
 * the join-tenant override, no session switch), then hand off to the
 * onboarding runner when the community requires it.
 *
 * Enablement + all copy are build-config driven (`env.joinCommunity`).
 */
export function JoinCommunity(): JSX.Element {
  const copy = joinConfig();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const coreUserId = user?.core_user_id ?? user?.coreUserId ?? '';
  const rootTenantId = getEffectiveRootTenantId(env.rootTenantId);

  const [step, setStep] = useState<Step>('search');
  const [selected, setSelected] = useState<TenantSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The override (X-Tenant-ID) stays set while a community is selected so
  // every join/onboarding call targets it; cleared when leaving the screen.
  useEffect(() => () => setJoinTenantContext(null), []);

  // Not reachable normally (entry is gated), but guard direct navigation.
  if (!copy.enabled || !rootTenantId) {
    return (
      <Screen copy={copy}>
        <Banner variant="info" title={`Joining a ${copy.entityName} isn’t available here.`} />
      </Screen>
    );
  }
  if (!coreUserId) {
    return (
      <Screen copy={copy}>
        <Banner
          variant="info"
          title="Single sign-on required"
          description={`Your account isn’t linked for cross-${copy.entityName} access.`}
        />
      </Screen>
    );
  }

  return (
    <Screen copy={copy}>
      {error ? <Banner variant="danger" title={error} className="mb-4" /> : null}

      {step === 'search' ? (
        <SearchStep
          copy={copy}
          rootTenantId={rootTenantId}
          coreUserId={coreUserId}
          onError={setError}
          onPending={(tenant) => {
            setSelected(tenant);
            setStep('pending');
          }}
          onProceed={(tenant) => {
            setSelected(tenant);
            setError(null);
            setStep('details');
          }}
        />
      ) : null}

      {step === 'details' && selected ? (
        <DetailsStep
          copy={copy}
          tenant={selected}
          coreUserId={coreUserId}
          rootTenantId={rootTenantId}
          user={{
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
          }}
          onError={setError}
          onJoined={(onboardingRoute) => {
            if (onboardingRoute) {
              navigate(onboardingRoute);
              return;
            }
            setStep('done');
          }}
        />
      ) : null}

      {step === 'pending' && selected ? (
        <Outcome
          icon={<Clock className="h-8 w-8" strokeWidth={1.5} />}
          title={copy.pendingTitle}
          message={copy.pendingMessage}
          tenantName={selected.name}
          onDone={() => navigate('/app/home')}
        />
      ) : null}

      {step === 'done' && selected ? (
        <Outcome
          icon={<CheckCircle2 className="h-8 w-8" strokeWidth={1.5} />}
          title={copy.successTitle}
          message={copy.successMessage}
          tenantName={selected.name}
          onDone={() => navigate('/app/home')}
        />
      ) : null}
    </Screen>
  );
}

function Screen({
  copy,
  children,
}: {
  copy: ReturnType<typeof joinConfig>;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="min-h-full bg-paper">
      <HeroHeader title={copy.title} subtitle={copy.subtitle} backTo="/app/home" />
      <div className="px-5 pt-5 pb-safe-or-6">{children}</div>
    </div>
  );
}

/* ── Step 1: search ─────────────────────────────────────────────────── */

function SearchStep({
  copy,
  rootTenantId,
  coreUserId,
  onProceed,
  onPending,
  onError,
}: {
  copy: ReturnType<typeof joinConfig>;
  rootTenantId: string;
  coreUserId: string;
  onProceed: (tenant: TenantSearchResult) => void;
  onPending: (tenant: TenantSearchResult) => void;
  onError: (message: string | null) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search.trim(), 300);
  const ready = debounced.length >= SEARCH_MIN;

  const query = useQuery({
    queryKey: ['join-tenant-search', rootTenantId, debounced],
    queryFn: () => searchTenantsByRoot(rootTenantId, debounced),
    enabled: ready,
  });
  const tenants = query.data ?? [];

  // When a community is picked, address subsequent calls to it and check for
  // an existing join request before showing the details form.
  const pick = useMutation({
    mutationFn: async (tenant: TenantSearchResult) => {
      setJoinTenantContext(tenant.tenant_id);
      const status = await fetchJoinStatus(coreUserId).catch(() => null);
      return { tenant, status };
    },
    onSuccess: ({ tenant, status }) => {
      const s = (status?.status ?? '').toLowerCase();
      if (s === 'pending' || s === 'approved') onPending(tenant);
      else onProceed(tenant);
    },
    onError: (e) => onError(toApiError(e).message),
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="font-display text-headline text-ink">{copy.searchTitle}</h2>
        <p className="mt-0.5 text-body-sm text-ink-secondary">{copy.searchSubtitle}</p>
      </div>

      <label className="flex items-center gap-2 rounded-pill border border-hairline-strong bg-card px-4 py-3">
        <Search className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={copy.searchPlaceholder}
          autoFocus
          className="w-full bg-transparent text-body text-ink placeholder:text-ink-muted focus:outline-none"
        />
      </label>

      {!ready ? (
        <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
          Type at least {SEARCH_MIN} characters to search.
        </p>
      ) : query.isLoading || pick.isPending ? (
        <>
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </>
      ) : tenants.length === 0 ? (
        <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
          No {copy.entityPlural} match “{debounced}”.
        </p>
      ) : (
        tenants.map((tenant) => {
          const logo = tenant.logo_small_url || tenant.logo_large_url;
          return (
            <button
              key={tenant.tenant_id}
              type="button"
              disabled={pick.isPending}
              onClick={() => pick.mutate(tenant)}
              className="flex items-center gap-3 rounded-2xl border border-hairline-strong bg-card p-3 text-left transition-colors hover:bg-sunken disabled:opacity-60"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary-50">
                {logo ? (
                  <img src={logo} alt="" className="h-full w-full object-contain" />
                ) : (
                  <Building2 className="h-5 w-5 text-primary-700" strokeWidth={1.75} />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body font-medium text-ink">{tenant.name}</span>
                {tenant.resolution_domain ? (
                  <span className="block truncate text-caption text-ink-muted">
                    {tenant.resolution_domain}
                  </span>
                ) : null}
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={1.75} />
            </button>
          );
        })
      )}
    </div>
  );
}

/* ── Step 2: details ────────────────────────────────────────────────── */

function DetailsStep({
  copy,
  tenant,
  coreUserId,
  rootTenantId,
  user,
  onJoined,
  onError,
}: {
  copy: ReturnType<typeof joinConfig>;
  tenant: TenantSearchResult;
  coreUserId: string;
  rootTenantId: string;
  user: { firstName?: string; lastName?: string; email?: string };
  onJoined: (onboardingRoute: string | null) => void;
  onError: (message: string | null) => void;
}): JSX.Element {
  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [phone, setPhone] = useState('');
  const [memberTypeId, setMemberTypeId] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Member types for the *target* tenant (override is already active).
  const memberTypesQuery = useQuery({
    queryKey: ['join-member-types', tenant.tenant_id],
    queryFn: fetchMemberTypes,
  });
  const memberTypes: MemberType[] = memberTypesQuery.data ?? [];

  const submit = useMutation({
    mutationFn: async () => {
      const memberData: JoinRequestMemberData = {
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        member_type_id: memberTypeId || undefined,
      };

      // Append the target tenant so the onboarding runner keeps addressing it.
      const withTenant = (base: string): string => {
        const sep = base.includes('?') ? '&' : '?';
        return `${base}${sep}joinTenant=${encodeURIComponent(tenant.tenant_id)}`;
      };

      const result = await joinTenant(coreUserId, memberData, rootTenantId);

      // The join request may echo the onboarding ids directly. A flow instance
      // id is immediately usable.
      if (result.flow_instance_id) {
        return withTenant(`/onboarding?instance=${encodeURIComponent(result.flow_instance_id)}`);
      }

      // Otherwise the onboarding can take a moment to provision — poll the
      // join onboarding-gate a few times before deciding (don't redirect early,
      // and don't skip onboarding that simply isn't ready yet).
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const gate = await fetchJoinOnboardingGate(coreUserId).catch(() => null);
        if (gate?.requires_onboarding) {
          const base = onboardingRouteForGate(gate);
          if (base) return withTenant(base);
        }
        await new Promise((resolve) => setTimeout(resolve, 1200));
      }

      // Onboarding not required (or never provisioned) — show the success state.
      return null;
    },
    onSuccess: (route) => onJoined(route),
    onError: (e) => onError(toApiError(e).message),
  });

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    acceptedTerms &&
    !submit.isPending;

  const termsUrl = env.signupTermsUrl;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3 rounded-2xl border border-hairline-strong bg-card p-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50">
          {tenant.logo_small_url || tenant.logo_large_url ? (
            <img
              src={tenant.logo_small_url || tenant.logo_large_url || ''}
              alt=""
              className="h-full w-full object-contain"
            />
          ) : (
            <Building2 className="h-5 w-5 text-primary-700" strokeWidth={1.75} />
          )}
        </span>
        <div className="min-w-0">
          <p className="truncate text-body font-semibold text-ink">{tenant.name}</p>
          <p className="text-caption text-ink-muted">{copy.detailsSubtitle}</p>
        </div>
      </div>

      <FormSection title={copy.detailsTitle}>
        <div className="grid grid-cols-2 gap-3">
          <TextInputField
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            required
          />
          <TextInputField
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            required
          />
        </div>
        <TextInputField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <TextInputField
          label="Mobile"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          optional
        />
        {memberTypes.length > 0 ? (
          <SelectField
            label="Member type"
            value={memberTypeId}
            onChange={(e) => setMemberTypeId(e.target.value)}
          >
            <option value="">Select…</option>
            {memberTypes.map((mt) => (
              <option key={mt.member_type_id} value={mt.member_type_id}>
                {mt.name}
              </option>
            ))}
          </SelectField>
        ) : null}
      </FormSection>

      <CheckboxOption
        label={
          termsUrl ? (
            <span>
              {copy.termsLabel}{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary-700 underline"
              >
                (view)
              </a>
            </span>
          ) : (
            copy.termsLabel
          )
        }
        checked={acceptedTerms}
        onChange={setAcceptedTerms}
      />

      <ActionButton variant="gold" disabled={!canSubmit} onClick={() => submit.mutate()}>
        {submit.isPending ? 'Setting up…' : copy.submitLabel}
      </ActionButton>
    </div>
  );
}

/* ── Outcome (pending / done) ───────────────────────────────────────── */

function Outcome({
  icon,
  title,
  message,
  tenantName,
  onDone,
}: {
  icon: ReactNode;
  title: string;
  message: string;
  tenantName: string;
  onDone: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-4 px-4 py-10 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
        {icon}
      </span>
      <div>
        <h2 className="font-display text-display-lg text-ink">{title}</h2>
        <p className="mt-1 text-body-sm text-ink-secondary">{message}</p>
        <p className="mt-2 text-body font-semibold text-ink">{tenantName}</p>
      </div>
      <ActionButton variant="gold" onClick={onDone} className="mt-2 w-full">
        Done
      </ActionButton>
    </div>
  );
}
